'use client';

import { MessageSquare } from 'lucide-react';
import {
  ANNOUNCEMENT_LIVE_SLOT,
  isAnnouncementScheduleActive,
  parseAnnouncementTone,
} from '@web-studio/shared-schemas';
import { EditableContent } from '../../edit/EditableContent';
import { LucideIcon } from '../../icons/LucideIcon';
import { resolveCtaLucideByIconKey } from '../../icons/ctaLucideMap';
import { pickBlockField } from '../../lib/localized';
import type { BlockRenderProps } from '../../types';

/**
 * Global chrome — temporary site-wide notice above navigation.
 * Show when Visible + message + schedule window allows “now”.
 * Schedule is baked; client/edge hide outside the window without republish of the clock.
 */
export function AnnouncementBar({
  block,
  locale,
  isActive,
  activeEditTarget,
  editable,
}: BlockRenderProps) {
  const message = pickBlockField(block.content, 'message', locale).trim();
  const ctaLabel = pickBlockField(block.content, 'ctaLabel', locale).trim();
  const ctaHref = pickBlockField(block.content, 'ctaHref', locale).trim();
  const tone = parseAnnouncementTone(pickBlockField(block.content, 'tone', locale));
  const startsAt = pickBlockField(block.content, 'startsAt', locale).trim();
  const endsAt = pickBlockField(block.content, 'endsAt', locale).trim();
  const iconKey = pickBlockField(block.content, 'sectionIcon', locale);
  const icon = resolveCtaLucideByIconKey(iconKey) ?? MessageSquare;
  const isDraftHidden = block.hidden === true;
  const hasMessage = Boolean(message);
  const scheduleOk = isAnnouncementScheduleActive(startsAt, endsAt);
  const target =
    editable && activeEditTarget
      ? activeEditTarget
      : null;

  /* Live / bake omit draft-hidden or empty. Studio keeps a muted ghost. */
  if (!editable && (isDraftHidden || !hasMessage || !scheduleOk)) {
    return null;
  }

  const showCta = Boolean(ctaLabel);
  const emptyGhost = editable && !hasMessage;

  return (
    <aside
      className={[
        'ws-float-chrome',
        'ws-announce',
        `ws-announce--${tone}`,
        isActive ? 'ws-block--active' : '',
        isDraftHidden ? 'ws-block--hidden' : '',
        emptyGhost ? 'ws-announce--empty' : '',
        !scheduleOk && editable ? 'ws-announce--outside-window' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      data-block-id={block.blockId}
      data-component={block.componentType}
      data-live-slot={ANNOUNCEMENT_LIVE_SLOT}
      data-announce-tone={tone}
      {...(startsAt ? ({ 'data-starts-at': startsAt } as const) : {})}
      {...(endsAt ? ({ 'data-ends-at': endsAt } as const) : {})}
      {...(isDraftHidden ? ({ 'data-block-hidden': 'true' } as const) : {})}
      role="region"
      aria-label={message || 'Announcement'}
    >
      <div className="ws-announce__inner">
        <EditableContent
          as="span"
          fieldKey="sectionIcon"
          editable={editable}
          activeEditTarget={target}
          className="ws-announce__icon-hit"
        >
          <LucideIcon icon={icon} size="md" className="ws-announce__icon" />
        </EditableContent>
        <EditableContent
          as="p"
          fieldKey="message"
          editable={editable}
          activeEditTarget={target}
          className="ws-announce__message"
        >
          {message || (editable ? 'Add announcement' : '')}
        </EditableContent>
        {showCta || editable ? (
          editable ? (
            <EditableContent
              as="span"
              fieldKey="ctaLabel"
              editable={editable}
              activeEditTarget={target}
              className="ws-announce__cta"
            >
              {ctaLabel || 'CTA'}
            </EditableContent>
          ) : ctaHref ? (
            <a className="ws-announce__cta" href={ctaHref}>
              {ctaLabel}
            </a>
          ) : (
            <span className="ws-announce__cta">{ctaLabel}</span>
          )
        ) : null}
      </div>
    </aside>
  );
}
